class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

/* Kept in sync with the closing --mega-menu-duration in component-mega-menu-panel.css.
   The `open` attribute has to outlive the closing transition, otherwise the browser
   pulls the panel out of rendering on the first frame and the animation never shows. */
const MEGA_MENU_CLOSE_DURATION = 220;

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.megaMenu = this.querySelector('details.mega-menu');
    this.openFrame = null;
    this.closeTimer = null;

    /* Opening can stay native — onToggle picks it up. Closing cannot: the native click
       drops `open` straight away, which takes the panel out of rendering before the
       closing transition gets a frame. */
    if (this.megaMenu) {
      this.megaMenu.querySelector('summary').addEventListener('click', (event) => {
        if (!this.megaMenu.hasAttribute('open')) return;
        event.preventDefault();
        this.close();
      });
    }

    /* The category review asked for the dropdown to fold out on hover instead of only
       on click (Huel's shop as the reference). Mouse only: on touch a hover would fire
       together with the tap and immediately toggle the panel back shut, and below 1100px
       the inline menu isn't shown at all (burger instead). */
    this.hoverCapable = window.matchMedia('(min-width: 1100px) and (hover: hover) and (pointer: fine)');
    this.closeTimeout = null;

    this.addEventListener('mouseenter', this.onMouseEnter.bind(this));
    this.addEventListener('mouseleave', this.onMouseLeave.bind(this));
  }

  onMouseEnter() {
    if (!this.hoverCapable.matches) return;
    clearTimeout(this.closeTimeout);
    this.open();
  }

  /* The panel hangs off the bottom of .header-wrapper, so the pointer crosses the
     header's own bottom padding on its way down from the label — geometrically outside
     this element. The delay bridges that gap; re-entering the panel (a DOM child, so it
     fires mouseenter here) cancels the pending close. */
  onMouseLeave() {
    if (!this.hoverCapable.matches) return;
    clearTimeout(this.closeTimeout);
    this.closeTimeout = setTimeout(() => this.close(), 200);
  }

  open() {
    clearTimeout(this.closeTimer);
    this.mainDetailsToggle.setAttribute('open', '');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', true);
    /* Re-opening inside the closing delay leaves `open` in place, so no toggle event
       fires and the reveal has to be kicked off here as well. */
    this.reveal();
  }

  close() {
    if (!this.megaMenu) return super.close();

    clearTimeout(this.closeTimer);
    cancelAnimationFrame(this.openFrame);
    this.megaMenu.classList.remove('mega-menu--open');
    this.megaMenu.querySelector('summary').setAttribute('aria-expanded', false);
    this.closeTimer = setTimeout(() => this.megaMenu.removeAttribute('open'), MEGA_MENU_CLOSE_DURATION);
  }

  /* Two frames: the first is where the browser starts rendering the panel at its closed
     values, the second is what the transition runs from. Flipping the class in the same
     frame as `open` collapses both into one style resolution and the panel just appears. */
  reveal() {
    if (!this.megaMenu || !this.megaMenu.hasAttribute('open')) return;
    cancelAnimationFrame(this.openFrame);
    this.openFrame = requestAnimationFrame(() => {
      this.openFrame = requestAnimationFrame(() => this.megaMenu.classList.add('mega-menu--open'));
    });
  }

  onToggle() {
    if (this.megaMenu) {
      if (this.megaMenu.hasAttribute('open')) {
        this.reveal();
      } else {
        /* Anything that drops `open` behind our back — Escape (global.js), a theme
           editor re-render — leaves the class stale, which would make the next open
           skip its animation. */
        cancelAnimationFrame(this.openFrame);
        this.megaMenu.classList.remove('mega-menu--open');
      }
    }

    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);
